/**
 * Google Analytics 4 — env-gated, prod-default. (Inlined, NOT @gjl/analytics.)
 *
 * Fairmeadow's frontend CI does a self-contained project-level `npm ci` and uses
 * tailwind v4; adding the `@gjl/analytics` *workspace* dep forces a root install
 * that breaks `@tailwindcss/vite`'s `tailwindcss` resolution (verified: two prod
 * deploys failed 2026-06-06). So this site keeps the inlined module. If its CI is
 * ever reworked to install at root with tailwind hoisting handled, migrate to the
 * package like the other sites.
 *
 * The fairmeadow.capital GA4 web stream. A Measurement ID is public (sent to the
 * browser), so it's baked as the prod default; .env is gitignored from CI.
 */

const GA_ID = (import.meta.env.VITE_GA_ID as string | undefined) || (import.meta.env.PROD ? 'G-CYSL89QH6Z' : undefined)

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

let initialized = false

/** Inject gtag.js and configure GA4. Safe to call once at startup. */
export function initAnalytics(): void {
  if (initialized || !GA_ID || typeof window === 'undefined') return
  initialized = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', GA_ID, { send_page_view: false })
}

/** Record a page view for the given SPA path. No-op when analytics is off. */
export function trackPageview(path: string): void {
  if (!GA_ID || typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  })
}

/** True when a measurement ID is configured (analytics is live). */
export const analyticsEnabled = Boolean(GA_ID)
